// tendersApi — wrapper around the shared axios `api` instance for the US
// government tender search endpoints. `api` already injects the Bearer token and
// refreshes it on 401 (see pages/chat/api.js), so these stay thin.
//
// Every endpoint is scoped to an organisation. The backend verifies membership
// rather than trusting the id, so passing one you do not belong to is refused
// with 403 instead of quietly falling back to your own workspace.
import Cookies from "js-cookie";

import api from "../pages/chat/api";

const BASE = "/api/v1/tenders/";

const withOrg = (params = {}) => {
  const orgId = Cookies.get("selected_org_id");
  return orgId ? { organization_id: orgId, ...params } : { ...params };
};

// GET /api/v1/tenders/
//   -> { count, limit, offset, has_more, results: [...], excluded }
//
// `excluded` is non-null when a filter is hiding records — currently tenders
// with no industry listed by the buyer. Show it: staying silent is how a user
// misses work they could have won.
export const searchTenders = async (filters = {}, { limit = 20, offset = 0, signal } = {}) => {
  const res = await api.get(BASE, { params: withOrg({ ...filters, limit, offset }), signal });
  return res.data;
};

// GET /api/v1/tenders/<id>/ -> full tender incl. documents, codes, other versions
export const fetchTender = async (tenderId) => {
  const res = await api.get(`${BASE}${tenderId}/`, { params: withOrg() });
  return res.data;
};

// Reads an error response back out of a blob-typed request. `responseType:
// "blob"` applies to the whole request, error responses included, so a 403's
// JSON body {"error": "..."} arrives as a Blob rather than parsed data —
// this is the one place that unwraps it back into the message we actually
// want to show.
const blobErrorMessage = async (err, fallback) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.error) return parsed.error;
    } catch {
      /* not JSON — fall through to the generic message */
    }
  }
  return err?.response?.data?.error || fallback;
};

// GET /api/v1/tenders/<id>/documents/<id>/download/ -> triggers a browser
// download of that one attachment's bytes.
//
// There's no plain URL to link to: a document's source_url is a signed link
// that lives about nine seconds and is resolved server-side only (see the
// backend's own TenderDocument docstring), so every download goes through
// this authenticated fetch-then-save, same as the CSV export elsewhere in
// this app — an <a href> can't carry the auth header this needs.
export const downloadTenderDocument = async (tenderId, documentId, suggestedName) => {
  try {
    const res = await api.get(`${BASE}${tenderId}/documents/${documentId}/download/`, {
      params: withOrg(),
      responseType: "blob",
    });
    const disposition = res.headers?.["content-disposition"] || "";
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || suggestedName || "document";

    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    throw new Error(await blobErrorMessage(err, "Could not download this file. Please try again."));
  }
};

// POST /api/v1/tenders/<id>/import/ -> the created import job, incl. folder_id
//
// `documentIds` undefined means "everything" (or live discovery when the bulk
// feed left the attachment list unknown); an explicit [] means "no files".
// The project exists as soon as this returns; downloads continue server-side.
export const importTender = async (tenderId, { documentIds, projectName } = {}) => {
  const body = {};
  if (documentIds !== undefined) body.document_ids = documentIds;
  if (projectName) body.project_name = projectName;
  const res = await api.post(`${BASE}${tenderId}/import/`, body, { params: withOrg() });
  return res.data;
};

// POST /api/v1/tenders/<id>/fit/
//   -> { gates, verdict, reasons, youd_need, last_time, subtitle, memory_ready }
//
// ON DEMAND, NOT ON PAGE LOAD. This is one model call against the workspace's
// Memory; there are 113,431 notices in the corpus and it runs when somebody asks
// about one of them. POST because it can spend money, even though it reads.
//
// The objective obstacles (`gates`) already arrive with fetchTender, so a tender
// one of them blocks never needs this call — the backend returns `skipped` rather
// than a verdict if asked anyway.
export const analyseTenderFit = async (tenderId) => {
  const res = await api.post(`${BASE}${tenderId}/fit/`, {}, { params: withOrg() });
  return res.data;
};

// GET /api/v1/tenders/imports/<id>/ -> job status with per-file states
export const fetchImportStatus = async (importId) => {
  const res = await api.get(`${BASE}imports/${importId}/`, { params: withOrg() });
  return res.data;
};

// GET /api/v1/tenders/imports/?folder_id=<id> -> that project's import, or null
//
// Navigation state does not survive a page refresh, so the project page asks
// for its own import rather than relying on being handed the id. Most projects
// were never imported, which answers 204 and is not an error.
export const fetchImportForFolder = async (folderId) => {
  const res = await api.get(`${BASE}imports/`, { params: withOrg({ folder_id: folderId }) });
  return res.status === 204 ? null : res.data;
};

// POST /api/v1/tenders/imports/<id>/retry/ -> the requeued job
//
// Retries only files that genuinely failed. Skips (restricted, links,
// unsupported types, duplicates) are facts no retry changes and are left
// alone. 409 means the job is still running; 400 means nothing failed.
export const retryImport = async (importId) => {
  const res = await api.post(`${BASE}imports/${importId}/retry/`, {}, { params: withOrg() });
  return res.data;
};

// GET /api/v1/tenders/facets/
//   -> { counts, notice_types, categories, orderings, capabilities }
//
// Counts are computed over the *current* filters, so a chip never promises
// results the filter would not return. `capabilities` says what the visible
// sources can actually populate — used to hide a filter that would match
// nothing at all (SAM.gov publishes no contract values, for instance).
export const fetchTenderFacets = async (filters = {}, { signal } = {}) => {
  const res = await api.get(`${BASE}facets/`, { params: withOrg(filters), signal });
  return res.data;
};

// --- Discover: tenders found for the workspace without a search ------------

// GET /api/v1/tenders/discover/?tab=&sort=
//   -> { results, count, has_more, stats, status, viewer, setup_done }
//
// `status.refreshing` means a match run is queued or running (first visit, or
// Memory / the match profile changed): poll until it clears.
export const fetchDiscover = async ({ tab = "for_you", sort = "best", limit = 20, offset = 0, signal } = {}) => {
  const res = await api.get(`${BASE}discover/`, { params: withOrg({ tab, sort, limit, offset }), signal });
  return res.data;
};

// POST — resets this person's "New" (the feed is shared, "New" is not).
export const markDiscoverViewed = async () => {
  await api.post(`${BASE}discover/viewed/`, {}, { params: withOrg() });
};

// POST — match again now. 429 when it ran within the hour.
export const refreshDiscover = async () => {
  const res = await api.post(`${BASE}discover/refresh/`, {}, { params: withOrg() });
  return res.data;
};

// PATCH — how this person hears about new matches: "bell" | "bell_email" | "off".
export const setDiscoverNotify = async (notify) => {
  const res = await api.patch(`${BASE}discover/viewer/`, { notify }, { params: withOrg() });
  return res.data;
};

// A bookmark, free. Starting a project is importTender.
export const saveMatch = async (matchId, saved = true) => {
  const res = await api.post(`${BASE}matches/${matchId}/save/`, { saved }, { params: withOrg() });
  return res.data;
};

// "Not a fit" for the whole workspace; dismissed=false restores it.
export const dismissMatch = async (matchId, { dismissed = true, reason = "", note = "" } = {}) => {
  const res = await api.post(
    `${BASE}matches/${matchId}/dismiss/`,
    { dismissed, reason, note },
    { params: withOrg() }
  );
  return res.data;
};

// GET / PATCH the workspace's match profile — pre-filled from Memory.
export const fetchMatchProfile = async () => {
  const res = await api.get(`${BASE}match-profile/`, { params: withOrg() });
  return res.data;
};

export const updateMatchProfile = async (patch) => {
  const res = await api.patch(`${BASE}match-profile/`, patch, { params: withOrg() });
  return res.data;
};

// GET codes by number or name ({ q }), or the codes next to one ({ near }) ->
//   [{ code, title, scheme, open_count }]. From the official titles; no model call.
export const searchMatchCodes = async ({ q, near } = {}, { signal } = {}) => {
  const res = await api.get(`${BASE}match-profile/codes/`, {
    params: withOrg(near ? { near } : { q }),
    signal,
  });
  return res.data.results || [];
};

// POST a draft profile -> { count }: open tenders it would search. No model call.
export const previewMatchProfile = async (draft, { signal } = {}) => {
  const res = await api.post(`${BASE}match-profile/preview/`, draft, { params: withOrg(), signal });
  return res.data;
};

// --- display helpers -------------------------------------------------------

// A quarter of federal tenders publish a closing time with no timezone, so the
// moment is genuinely unknown. `deadline_precision` says which we have:
//   "datetime" -> show date and time
//   "date"     -> show the date only
//   null       -> no deadline published
// Showing a time we cannot vouch for is how a bid gets submitted late.
export const formatDeadline = (tender) => {
  if (!tender?.closes_at || !tender?.deadline_precision) return "No closing date";

  const when = new Date(tender.closes_at);
  const date = when.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  if (tender.deadline_precision === "date") return date;

  const time = when.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date}, ${time}${tender.closes_at_tz ? ` ${tender.closes_at_tz}` : ""}`;
};

// Urgency for the deadline column. Kept here so the list and the detail page
// cannot drift apart on what counts as urgent.
export const deadlineUrgency = (tender) => {
  const days = tender?.days_remaining;
  if (days == null) return "none";
  if (days < 0) return "passed";
  if (days <= 7) return "urgent";
  if (days <= 14) return "soon";
  return "ok";
};

export const formatDaysRemaining = (tender) => {
  const days = tender?.days_remaining;
  if (days == null) return "";
  if (days < 0) return "Closed";
  if (days === 0) return "Closes today";
  return `${days} day${days === 1 ? "" : "s"} left`;
};
